import { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../../services/axios-client";

const useTopRemark = (userKey) => {
    // Create user-specific initial state
    const getInitialPayload = () => ([
        {
            ID: '',
            Initiative: '',
            Description: '',
            StartDate: null,
            EndDate: null,
            Result: '',
            Comment: '',
            EvidenceName: '',
            Evidence: '',
            EvidenceLink: '',
            IsInitiative: false,
            IsProject: false,
        },
    ]);

    const [payload, setPayload] = useState(getInitialPayload)
    const [deleted, setDeleted] = useState([])
    const [items, setItems] = useState()
        const [activeKey, setActiveKey] = useState(1)
        const [isSaving, setIsSaving] = useState(false)

    // Track current user but don't reset payload immediately
    // Let TopRemark component handle localStorage restoration first
    const [currentUser, setCurrentUser] = useState(userKey);
    if (currentUser !== userKey) {
        // Only reset deleted items and active key, not payload/items
        // Payload will be set by localStorage restoration or API call
        setDeleted([]);
        setActiveKey(1);
        setCurrentUser(userKey);
    }
    const handleOnChanges = (e, index) => {
        const { name, value, checked } = e.target;
        const data = [...payload]
        if (e.target.type === "checkbox") {
            data[index][name] = checked
        } else {
            data[index][name] = value
        }
        setPayload(data)
    }

    const onChangeActiveKey = (newActiveKey) => {
        setActiveKey(newActiveKey);
    };

    const handleFileChange = (e, index) => {
        const file = e.target.files[0];
        const name = e.target.name;
        const data = [...payload];

        // Check if file size is less than or equal to 10MB

        // Define allowed file types
        const allowedTypes = [
            // PDF
            "application/pdf",
            // Word documents
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            // Excel documents
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            // PowerPoint documents
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            // ZIP files
            "application/zip",
            "application/x-zip-compressed",
            // Images
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/bmp",
            "image/webp"
        ];

        if (!allowedTypes.includes(file.type)) {
            toast.error("Only PDF, Word, Excel, PowerPoint, ZIP, and image files are allowed.");
            return;
        }

        if (file.size <= 10 * 1024 * 1024) {
            data[index][name] = file;
            setPayload(data);
        } else {
            // Handle case where file size exceeds 10MB
            toast.error("File size exceeds 10MB limit. Please choose a smaller file.");
            return;
        }
    };

    const onDeleteInitiative = (removedIndex, initiativeID, thisPayload) => {
        let filtered = []
        if (initiativeID !== "") {
            filtered = thisPayload.filter((item) => item.ID !== initiativeID);
            setDeleted([...deleted, initiativeID])
        } else {
            filtered = thisPayload.filter((_, index) => {
                // console.log(index, removedIndex)
                return index !== removedIndex;
            });
            setDeleted([...deleted, initiativeID])
        }
        setPayload(filtered)
        setItems(items - 1)
        onChangeActiveKey(items - 1)
    }

    const onClickAddInitiative = () => {
        if (payload.length < 10) {
            let dataPayload = [...payload]
            dataPayload.push({
                ID: '',
                Initiative: '',
                Description: '',
                StartDate: null,
                EndDate: null,
                Result: '',
                Comment: ''
            })
            setPayload(dataPayload)
            setItems(items + 1)
            onChangeActiveKey(items + 1)
        } else {
            toast.error("You cannot have more than 10 initiatives")
        }
    }

    // Method to reset payload to initial state
    const resetToInitialState = () => {
        setPayload(getInitialPayload());
        setItems(1);
        setActiveKey(1);
    };

    const getTopRemarks = async (projectID, employeeID, projectPhaseID) => {
        try {
            setDeleted([])
            // Don't reset payload immediately - check if we should use it from localStorage first
            let res = await axiosInstance.get(`/auth/top-remark/${projectID}/${employeeID}/${projectPhaseID}`)
            if (res.status === 200) {
                if (res.data.data) {
                    let allTopRemarks = res.data.data
                    for (let data of allTopRemarks) {
                        if (data.StartDate) {
                            const startD = new Date(data.StartDate);
                            data.StartDate = startD.toISOString().split('T')[0].slice(0, 7);
                        }
                        if (data.EndDate) {
                            const endD = new Date(data.EndDate);
                            data.EndDate = endD.toISOString().split('T')[0].slice(0, 7);
                        }
                    }
                    setPayload(allTopRemarks)
                    setItems(res.data.data.length)
                } else {
                    // Only reset if no API data
                    resetToInitialState();
                }
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const postTopRemarks = async (projectID, employeeID, projectPhaseID, handleClose) => {
        try {
            setIsSaving(true)
            let sendPayload = [...payload]
            for (const data of sendPayload) {
                if (data.StartDate) {
                    data.StartDate = new Date(data.StartDate).toISOString()
                }
                if (data.EndDate) {
                    data.EndDate = new Date(data.EndDate).toISOString()
                }
                data.ProjectID = projectID
                data.EmployeeID = employeeID
                data.ProjectPhaseID = projectPhaseID
            }

            const formData = new FormData();
            sendPayload.forEach((payloadFile, index) => {
                formData.append(`Evidence_${index}`, payloadFile.Evidence);
            })

            sendPayload.forEach((payloadFile) => {
                delete payloadFile.Evidence
            });
            formData.append("payloads", JSON.stringify(sendPayload))

            // console.log(formData)
            let res = await axiosInstance.post(`/auth/top-remark/project`, formData)
            if (res.status === 200) {
                toast.success("Successfully save justification")
                handleClose()
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        } finally {
            setIsSaving(false)
        }
    }

    const deleteTopRemark = async () => {
        try {
            if (deleted.length > 0) {
                let res = await axiosInstance.post('/auth/top-remark/delete', { IDs: deleted })
                if (res.status === 204) {
                    toast.success("Successfully delete justification")
                }
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const isValid = (text, minimumLength) => text.length >= minimumLength;

    const checkBeforePostInitiative = (confirmationAPlus) => {
        let bool = false
        for (const data of payload) {
            if (data.Initiative !== "" && data.Description !== "" && data.Result !== "") {
                bool = true
                if (
                    isValid(data.Initiative, 10) &&
                    isValid(data.Description, 20) &&
                    isValid(data.Result, 10)) {
                    if (confirmationAPlus) {
                        if (data.IsProject || data.IsInitiative) {
                            bool = true
                        } else {
                            bool = false
                            toast.error("You should fill one of the Innovation Project or Beyond Job Scope")
                        }
                    }
                } else {
                    bool = false
                    toast.error("You should fill the minimum length of each fields")
                }
            } else {
                bool = false
                toast.error("You should fill all field for each Initiative")
                break
            }
        }
        return bool
    }

    return {
        payload,
        items,
        setItems,
        setPayload,
        resetToInitialState,
        handleOnChanges,
        onDeleteInitiative,
        onClickAddInitiative,
        getTopRemarks,
        postTopRemarks,
        deleteTopRemark,
        checkBeforePostInitiative,
        handleFileChange,
        deleted,
        activeKey,
        onChangeActiveKey,
        isSaving,
    }
}

export default useTopRemark